import React, { useCallback, useEffect } from "react";

// components
import Button from "../../../component/element/forms/Button";
import {
  FaEdit,
  FaClock,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaMoneyBillWave,
} from "react-icons/fa";

// constants
import { CgArrowLongLeft } from "react-icons/cg";
import { BiWorld } from "react-icons/bi";

import { format_date_time } from "../../../services/convert/date";

// state
import { useNavigate, useParams } from "react-router-dom";
import { get_training } from "../../../services/apis/training";
import { useAppDispatch, useAppSelector } from "../../../store";
import { get_countries, get_states } from "../../../services/apis/locations";
import { FaLocationDot } from "react-icons/fa6";
import { LuLoader } from "react-icons/lu";

const ViewTraining = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const training = useAppSelector(({ training }) => training.training);
  const loading = useAppSelector(({ training }) => training.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const states = useAppSelector(({ locations }) => locations.states);
  const stateId = training?.state.id;
  const countryId = training?.country.id;
  const countryName = countries.find(
    (country) => String(country.id) === String(countryId),
  )?.name;
  const stateName = states.find(
    (state) => String(state.id) === String(stateId),
  )?.name;

  const onLoad = useCallback(() => {
    if (id) get_training(dispatch, id);
  }, [dispatch, id]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  useEffect(() => {
    if (countries.length === 0) {
      get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    if (countryId !== undefined && countryId !== null) {
      get_states(dispatch, countryId);
    }
  }, [countryId, dispatch]);

  return (
    <div>
      {/* Header Section with Navigation */}

      <div className="flex items-center justify-end space-x-3 mb-6">
        <Button
          title="Edit"
          onClick={() => navigate("/training/edit/" + id)}
          children={
            <>
              {loading && <LuLoader className="mr-1" />}
              <FaEdit className="mr-2" />
            </>
          }
          className="text-white transition-colors w-32"
        />
        <Button
          title="Back"
          onClick={() => navigate(-1)}
          children={<CgArrowLongLeft className="w-6 h-6 mr-2" />}
          className="mr-4 text-white transition-colors w-32"
          aria-label="Back"
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Image Section */}
        <div className="lg:w-1/2">
          <div className="relative h-0 pb-[56.25%] overflow-hidden rounded-xl shadow-md bg-gray-200">
            {training?.image ? (
              <img
                src={training?.image}
                alt={training?.name || "Training"}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                No image available
              </div>
            )}
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-gray-800 mt-3">
            {training?.name || "Training Details"}
          </h1>
        </div>

        {/* Details Section */}
        <div className="lg:w-1/2 space-y-6">
          {/* Key Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Price */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-center text-primary-600 mb-1">
                <FaMoneyBillWave className="mr-2" />
                <span className="text-sm font-medium">Price</span>
              </div>
              <p className="text-xl font-bold">{training?.amount || "N/A"}</p>
            </div>

            {/* Location */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-center text-primary-600 mb-1">
                <FaMapMarkerAlt className="mr-2" />
                <span className="text-sm font-medium">Location</span>
              </div>
              <p className="text-gray-800 font-medium truncate">
                {[stateName, countryName].filter(Boolean).join(", ") || "N/A"}
              </p>
            </div>

            {/* Date */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-center text-primary-600 mb-1">
                <FaCalendarAlt className="mr-2" />
                <span className="text-sm font-medium">Date</span>
              </div>
              <p className="text-gray-800 font-medium">
                {training?.date ? format_date_time(training?.date) : "TBD"}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="flex items-center text-primary-600 mb-1">
                <FaClock className="mr-2" />
                <span className="text-sm font-medium">Duration</span>
              </div>
              <p className="text-gray-800 font-medium">
                {training?.duration || "N/A"}
              </p>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
            <div className="flex items-center text-primary-600 mb-1">
              <FaLocationDot className="mr-2" />
              <span className="text-lg font-semibold">Address</span>
            </div>
            <p className="text-gray-700">
              {training?.address || "No address provided"}
            </p>
          </div>

          {/* Virtual Link */}
          {training?.link && (
            <div className="flex">
              <a
                href={training.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 hover:text-primary-800 transition-colors"
              >
                <BiWorld className="w-5 h-5 mr-2" />
                <span className="font-medium">Join Virtual Training</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Description Section */}
      <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-xl font-bold mb-4 text-gray-800">
          Training Description
        </h2>
        <div className="text-base text-gray-700">
          {training?.body ? (
            <div dangerouslySetInnerHTML={{ __html: training.body }} />
          ) : (
            <p>No description available for this training.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewTraining;
